import { ref, onMounted, onBeforeUnmount } from "vue"


export default {
  __name: 'footerSubscription',
  setup(__props) {

const formContainer = ref(null)
const STYLE_ID = "lc-footer-subscribe-override"

let observer = null
let timeoutId = null
let viewportObserver = null

/**
 * The subscribe form is an ActiveCampaign embed (form #76). AC injects the
 * form markup AND its own <style> at runtime, keyed to a per-form id hash
 * (e.g. #_form_6A588D4C8A2DC_) that changes whenever the form is edited in AC.
 *
 * So we don't hardcode that id: we read it from the injected <form> at runtime
 * and build the override CSS against the live id. Appending our <style> to
 * <head> after AC's, with matching (id) specificity + !important, deterministically
 * wins — giving the proof layout: title on top, then the email field growing to
 * fill the row with the Submit button aligned beside it.
 */
const buildCss = (id) => `
#${id} {
  font-family: "Inter", sans-serif !important;
}
#${id}._form_76 {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  gap: 0.5rem !important;
}
#${id} ._form-content {
  display: flex !important;
  flex-wrap: wrap !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem !important;
}
#${id} ._form-title {
  width: 100% !important;
  font-family: "Satoshi", sans-serif !important;
  font-size: 0.875rem !important;
  font-weight: 700 !important;
  color: var(--white) !important;
  margin: 0 !important;
  text-align: center !important;
}
#${id} ._form_element {
  margin: 0 !important;
}
#${id} ._form-label,
#${id} ._form_element label {
  display: none !important;
}
#${id} ._field-wrapper {
  width: 100% !important;
  max-width: 100% !important;
}
#${id} input[type="text"],
#${id} input[type="email"] {
  height: 40px !important;
  padding: 0 12px !important;
  border: 1px solid var(--gray--200) !important;
  border-radius: 8px !important;
  background: #fff !important;
  color: var(--black--100) !important;
  font: inherit !important;
  width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}
/* AC sets "#id ._x83600085 input { margin-top:8px !important }" at (1,1,1).
   Beat it with (1,2,1) using stable classes (not the volatile _x hash). */
#${id} ._form_element ._field-wrapper input {
  margin: 0 !important;
}

#${id} ._button-wrapper {
  margin: 0 !important;
  padding: 0 !important;
  display: inline-block !important;
}
#${id} ._submit,
#${id} button[type="submit"] {
  height: 40px !important;
  margin: 0 !important;
  padding: 0 18px !important;
  border: none !important;
  border-radius: 8px !important;
  background: var(--green) !important;
  color: #fff !important;
  font: inherit !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  width: auto !important;
  display: inline-block !important;
}
#${id} ._form-thank-you {
  color: #fff !important;
  font-size: 1rem !important;
}
`

const injectOverride = (id) => {
  const css = buildCss(id)
  let styleEl = document.getElementById(STYLE_ID)

  if (styleEl) {
    styleEl.textContent = css
  } else {
    styleEl = document.createElement("style")
    styleEl.id = STYLE_ID
    styleEl.textContent = css
    // Append last so we win cascade order ties against AC's injected <style>.
    document.head.appendChild(styleEl)
  }
}

const tryApply = () => {
  const container = formContainer.value
  if (!container) return false

  const form = container.querySelector("form[id^='_form_']")
  if (!form || !form.id) return false

  injectOverride(form.id)
  return true
}

/**
 * Pulls in the ActiveCampaign embed. Kept out of onMounted so it can be
 * deferred: this is a third-party origin with a slow response that then chains
 * to a Bunny Fonts stylesheet, and it used to sit on the critical path of every
 * page — for a form nobody sees until they reach the bottom.
 */
const loadEmbed = () => {
  const container = formContainer.value
  if (!container) return

  const formDiv = document.createElement("div")
  formDiv.className = "_form_76"
  container.appendChild(formDiv)

  // Watch for AC injecting the <form>, then style it against its real id.
  observer = new MutationObserver(() => {
    if (tryApply() && observer) {
      observer.disconnect()
      observer = null
    }
  })
  observer.observe(container, { childList: true, subtree: true })

  const script = document.createElement("script")
  script.src = "https://logfurnitureandmore.activehosted.com/f/embed.php?id=76"
  script.charset = "utf-8"
  container.appendChild(script)

  // In case the form was already present, or as a belt-and-suspenders fallback.
  tryApply()
  timeoutId = setTimeout(() => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }, 10000)
}

onMounted(() => {
  const container = formContainer.value
  if (!container) return

  // No IntersectionObserver (or a very old browser): load immediately rather
  // than risk a footer form that never appears.
  if (!("IntersectionObserver" in window)) {
    loadEmbed()
    return
  }

  // 600px of margin so the fetch starts while the footer is still being
  // scrolled towards — the embed is a lead-capture form, so it must be ready
  // before the reader gets there, not after.
  //
  // A reload with the page already at the bottom is covered: the initial
  // observation reports elements that are ALREADY intersecting, so the callback
  // fires without any scrolling. Scroll restoration lands either side of this
  // and works out the same.
  viewportObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((e) => e.isIntersecting)) return
      viewportObserver.disconnect()
      viewportObserver = null
      loadEmbed()
    },
    { rootMargin: "600px 0px" }
  )
  viewportObserver.observe(container)
})

onBeforeUnmount(() => {
  if (viewportObserver) {
    viewportObserver.disconnect()
    viewportObserver = null
  }
  if (observer) {
    observer.disconnect()
    observer = null
  }
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
})

return { __sfc: true,formContainer, STYLE_ID, observer, timeoutId, viewportObserver, buildCss, injectOverride, tryApply, loadEmbed }
}

}